/**
 * Ứng dụng Zalo riêng của tổ chức: OA kết nối qua app của chính khách → tin ZNS trừ vào
 * ví ZBS liên kết với app đó. Không cấu hình thì dùng app chung của nền tảng.
 * App Secret chỉ gửi lên (mã hoá phía máy chủ), không bao giờ đọc lại.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, KeyRound, Loader2 } from 'lucide-react'
import { api, apiError } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

interface ThongTinApp {
  appIdRieng: string | null
  coSecret: boolean
  dangDungRieng: boolean
  appIdChung: string | null
  callbackUrl: string | null
  webhookUrl: string
  oa: Array<{ id: string; ten: string | null; trangThai: string; appId: string | null }>
}

const KHOA = ['integrations', 'zalo-oa', 'app-config']

function DongSaoChep({ nhan, giaTri }: { nhan: string; giaTri: string | null }) {
  if (!giaTri) return null
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 shrink-0 text-muted-foreground">{nhan}</span>
      <code className="min-w-0 flex-1 truncate rounded bg-muted px-1.5 py-0.5">{giaTri}</code>
      <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => { void navigator.clipboard.writeText(giaTri); toast.success('Đã sao chép') }}><Copy className="h-3 w-3" /></Button>
    </div>
  )
}

export function UngDungZaloRieng() {
  const qc = useQueryClient()
  const { data } = useQuery<ThongTinApp>({ queryKey: KHOA, queryFn: async () => (await api.get<ThongTinApp>('/zalo-oa/app-config')).data })
  const [mo, setMo] = useState(false)
  const [appId, setAppId] = useState('')
  const [secret, setSecret] = useState('')

  const luu = useMutation({
    mutationFn: async () => (await api.put<ThongTinApp>('/zalo-oa/app-config', { appId: appId.trim(), appSecret: secret.trim() || undefined })).data,
    onSuccess: (d) => { qc.setQueryData(KHOA, d); setSecret(''); setMo(false); toast.success('Đã lưu ứng dụng Zalo riêng — bấm "Kết nối OA" để OA cấp quyền cho ứng dụng này') },
    onError: (e) => toast.error(apiError(e)),
  })
  const xoa = useMutation({
    mutationFn: async () => (await api.delete<ThongTinApp>('/zalo-oa/app-config')).data,
    onSuccess: (d) => { qc.setQueryData(KHOA, d); toast.success('Đã về dùng ứng dụng chung của hệ thống') },
    onError: (e) => toast.error(apiError(e)),
  })

  if (!data) return null
  const appHienTai = data.dangDungRieng ? data.appIdRieng : data.appIdChung
  const oaLechApp = data.oa.filter((o) => o.appId && o.appId !== appHienTai)

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Ứng dụng Zalo dùng để kết nối OA</span>
          {data.dangDungRieng ? <Badge variant="success">Ứng dụng riêng · {data.appIdRieng}</Badge> : <Badge variant="secondary">Ứng dụng chung của hệ thống</Badge>}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => { setAppId(data.appIdRieng ?? ''); setMo((v) => !v) }}>{data.dangDungRieng ? 'Đổi ứng dụng' : 'Dùng ứng dụng riêng'}</Button>
          {data.dangDungRieng && <Button size="sm" variant="ghost" disabled={xoa.isPending} onClick={() => { if (confirm('Về dùng ứng dụng chung? OA đang kết nối qua ứng dụng riêng vẫn chạy tới khi kết nối lại.')) xoa.mutate() }}>Về ứng dụng chung</Button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Ứng dụng riêng của doanh nghiệp (developers.zalo.me) liên kết với ví ZBS của chính doanh nghiệp → tin ZNS trừ vào ví đó. Không cấu hình thì dùng ứng dụng chung của hệ thống.</p>

      {mo && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <div><Label className="text-xs" htmlFor="oa-app-id">App ID</Label><Input id="oa-app-id" value={appId} onChange={(e) => setAppId(e.target.value.replace(/\D/g, ''))} className="h-8" placeholder="vd 2733897848308037270" /></div>
            <div><Label className="text-xs" htmlFor="oa-app-secret">App Secret {data.coSecret && data.appIdRieng === appId && <span className="text-muted-foreground">(để trống = giữ nguyên)</span>}</Label><Input id="oa-app-secret" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} className="h-8" placeholder="Khoá bí mật của ứng dụng" /></div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium">Cấu hình trong ứng dụng Zalo (developers.zalo.me) trước khi kết nối:</p>
            <DongSaoChep nhan="Callback URL" giaTri={data.callbackUrl} />
            <DongSaoChep nhan="Webhook URL" giaTri={data.webhookUrl} />
            <p className="text-[11px] text-muted-foreground">Bật quyền quản lý OA, gửi tin OA và ZNS; đăng ký sự kiện webhook nhận tin / quan tâm; liên kết ứng dụng vào tài khoản ZBS có ví (account.zalo.solutions → Quản lý tài sản).</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setMo(false)}>Huỷ</Button>
            <Button size="sm" disabled={luu.isPending || appId.length < 6} onClick={() => luu.mutate()}>{luu.isPending && <Loader2 className="animate-spin" />} Lưu ứng dụng</Button>
          </div>
        </div>
      )}

      {oaLechApp.length > 0 && (
        <p className="text-xs text-warning">
          {oaLechApp.map((o) => o.ten ?? 'OA').join(', ')} đang kết nối qua ứng dụng {oaLechApp[0].appId} — bấm "Kết nối OA" và chọn lại OA để chuyển sang ứng dụng hiện tại.
        </p>
      )}
    </div>
  )
}
